<template>
  <DataTable
    ref="dt"
    v-model:selection="selectedDealerships"
    v-model:filters="filters"
    export-filename="Dealerships"
    :value="modelValue"
    :loading="isLoading"
    removable-sort
    sort-field="id"
    :sort-order="-1"
    :rows="20"
    data-key="id"
    column-resize-mode="fit"
    :paginator="true"
    responsive-layout="scroll"
    filter-display="menu"
    :rows-per-page-options="[10, 20, 50]"
    state-key="dt-settings-dealerships"
    state-storage="session"
    current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
    :global-filter-fields="['id', 'displayName', 'callCenters.name', 'groupName', 'manufacturerName']"
    :export-function="formatExportData"
  >
    <template #empty>No records found</template>
    <template #header>
      <div class="flex justify-between">
        <span class="flex justify-between gap-4">
          <span>
            <Input
              v-model="filters['global'].value"
              :placeholder="$t('ui.common.search', 1)"
              icon="search"
            />
          </span>
          <Button
            :label="$t('ui.actions.clear', 1)"
            severity="secondary"
            type="button"
            outlined
            icon="filter-slash"
            @click="clearFilter()"
          />
        </span>
      </div>
      <div class="flex justify-between mt-4">
        <span>
          <Button
            label="CSV"
            type="button"
            severity="secondary"
            outlined
            icon="file-excel"
            @click="exportCSV"
          />
        </span>
      </div>
    </template>

    <!-- <Column selection-mode="multiple" header-style="width: 3em"></Column> -->

    <Column
      field="id"
      header="ID"
      :sortable="true"
    >
      <template #body="{ data }">
        {{ data.id }}
      </template>
    </Column>

    <Column
      field="displayName"
      header="Name"
      :sortable="true"
    >
      <template #body="{ data }">
        <AvatarLabelGroup
          :label="data.displayName"
          :description="data.org?.name ?? data.groupName"
          :avatar-url="data.branding?.logoUrl"
          avatar-is-logo
          :label-link="'/settings/dealerships/' + data.id"
          class="whitespace-nowrap"
        />
      </template>
    </Column>

    <Column
      field="type"
      header="Type"
      :sortable="true"
      :show-filter-match-modes="false"
      :show-filter-operator="false"
    >
      <template #body="{ data }">
        <Chip
          v-if="data.type"
          :rounded="false"
          outlined
          size="sm"
        >
          {{ dealershipTypeLabels[data.type] ?? data.type }}
        </Chip>
      </template>
      <template #filter="{ filterModel }">
        <Select
          :teleport="false"
          v-model="filterModel.value"
          :options="dealershipTypeOptions"
          mode="multiple"
          chips
          value-key="value"
          label-key="label"
          placeholder="All"
        >
          <template #option="slotProps">
            {{ slotProps.option.label }}
          </template>
        </Select>
      </template>
    </Column>

    <Column
      field="callCenters"
      header="Call Centers"
      :sortable="true"
      :show-filter-match-modes="false"
      :show-filter-operator="false"
    >
      <template #body="{ data }">
        <Chip
          v-for="(item, index) in callCenterChips(data)"
          :key="index"
          v-bind="item"
          class="link-hover"
          @click="$router.push('/settings/call-centers/' + item.id)"
        />
      </template>
      <template
        v-if="callCentersAllowed?.length > 1"
        #filter="{ filterModel }"
      >
        <Select
          :teleport="false"
          v-model="filterModel.value"
          :options="callCentersAllowed"
          searchable
          mode="multiple"
          chips
          value-key="id"
          label-key="name"
          placeholder="All"
        >
          <template #option="slotProps">
            {{ slotProps.option.name }}
          </template>
        </Select>
      </template>
    </Column>

    <Column
      field="billingIsActive"
      header="Billing Status"
      :sortable="true"
      :show-filter-match-modes="false"
      :show-filter-operator="false"
    >
      <template #body="{ data }">
        <Chip
          v-if="data.billingIsActive"
          severity="success"
          :rounded="false"
          size="sm"
        >
          Active
        </Chip>
        <Chip
          v-else
          :rounded="false"
          size="sm"
        >
          Disabled
        </Chip>
      </template>
      <template #filter="{ filterModel }">
        <Select
          :teleport="false"
          v-model="filterModel.value"
          :options="[
            { value: true, label: 'Active' },
            { value: false, label: 'Disabled' }
          ]"
          searchable
          mode="multiple"
          chips
          value-key="value"
          label-key="label"
          placeholder="All"
        >
          <template #option="slotProps">
            {{ slotProps.option.label }}
          </template>
        </Select>
      </template>
    </Column>

    <Column
      field="isActive"
      header="Status"
      :sortable="true"
      :show-filter-match-modes="false"
      :show-filter-operator="false"
    >
      <template #body="{ data }">
        <ToggleSwitch
          v-if="$can('manage_dealerships')"
          v-model="data.isActive"
          @change="val => toggleIsActive(val, data.id)"
        />
        <Chip
          v-else-if="data.isActive"
          severity="success"
          :rounded="false"
          size="sm"
        >
          Active
        </Chip>
        <Chip
          v-else
          :rounded="false"
          size="sm"
        >
          Disabled
        </Chip>
      </template>
      <template #filter="{ filterModel }">
        <Select
          :teleport="false"
          v-model="filterModel.value"
          :options="[
            { value: true, label: 'Active' },
            { value: false, label: 'Disabled' }
          ]"
          searchable
          mode="multiple"
          chips
          value-key="value"
          label-key="label"
          placeholder="All"
        >
          <template #option="slotProps">
            {{ slotProps.option.label }}
          </template>
        </Select>
      </template>
    </Column>

    <Column
      header=""
      :sortable="false"
    >
      <template #body="slotProps">
        <div class="flex">
          <Button
            v-tippy="$t('ui.entities.setting', 2)"
            icon="cog"
            plain
            severity="secondary"
            @click="$router.push('/settings/dealerships/' + slotProps.data.id)"
          ></Button>
        </div>
      </template>
    </Column>
  </DataTable>
</template>

<script setup>
import Button from '@/components/button/Button.vue'
import Input from '@/components/forms/Input.vue'
import AvatarLabelGroup from '@/components/avatar/AvatarLabelGroup.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
import Chip from '@/components/chip/Chip.vue'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Select from '@/components/forms/Select.vue'

import useApiRequest from '@/composables/useApiRequest'
import { alertToast } from '@/utilities/notification'
import { useCallCenterStore } from '@/stores/CallCenterStore'

import { ref, onMounted } from 'vue'
import { FilterMatchMode, FilterService } from '@primevue/core/api'

const props = defineProps({
  modelValue: {
    type: Array,
    required: true
  },
  isLoading: {
    type: Boolean,
    required: true
  }
})
defineEmits(['dealership-updated'])
const dt = ref({ processedData: [] })
const dealershipApiRequest = useApiRequest()

const callCenterStore = useCallCenterStore()
const callCentersAllowed = callCenterStore.callCenters

const selectedDealerships = ref([])

onMounted(() => {
  FilterService.register(callCenterFilter.value, (value, filter) => {
    if (!filter.length) {
      return true
    }

    return value.some(callCenter => filter.includes(callCenter.id))
  })
})

const callCenterFilter = ref('Call Center Filter')

// UI labels for the dealership type enum
const dealershipTypeLabels = {
  franchised: 'Single Brand',
  multi_brand: 'Multi-Brand',
  used_supermarket: 'Used Supermarket'
}
const dealershipTypeOptions = Object.entries(dealershipTypeLabels).map(([value, label]) => ({ value, label }))

const filters = ref(null)

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    callCenters: { value: null, matchMode: callCenterFilter.value },
    billingIsActive: { value: null, matchMode: FilterMatchMode.IN },
    isActive: { value: null, matchMode: FilterMatchMode.IN },
    type: { value: null, matchMode: FilterMatchMode.IN },
    groupName: { value: null, matchMode: FilterMatchMode.IN },
    manufacturerName: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

const clearFilter = () => {
  initFilters()
}

function toggleIsActive(val, id) {
  dealershipApiRequest
    .send({
      endpoint: '/v1/dealerships/' + id,
      method: 'PATCH',
      data: {
        isActive: val
      }
    })
    .then(response => {
      if (!response.success) {
        alertToast('Dealership could not be updated', response.data.message, 'error')
        // Set value to what it was before toggle
        const dealership = props.modelValue.find(d => d.id == id)
        dealership.isActive = !val
      }
    })
}

// Call center chips
const callCenterChips = data => {
  return data.callCenters.map(callCenter => {
    return {
      id: callCenter.id,
      label: callCenter.name,
      severity: 'default',
      icon: null,
      size: 'sm',
      rounded: true,
      tooltip: null
    }
  })
}

/**
 * Format data for CSV export
 * @param {Object} params - Export parameters
 * @param {any} params.data - The data value for the field
 * @param {string} params.field - The field name
 * @returns {string} Formatted value for export
 */
function formatExportData({ data, field }) {
  if (field === 'callCenters') {
    // Convert call centers array to comma-separated string
    if (Array.isArray(data) && data.length > 0) {
      return data.map(cc => cc.name || cc.label || String(cc)).join(', ')
    }
    return ''
  }

  if (field === 'billingIsActive' || field === 'isActive') {
    // Convert boolean to 'active' or 'deactive'
    return data === true ? 'active' : 'deactive'
  }

  // Return data as-is for other fields (including ID)
  return data
}

/**
 * Export dealerships data as CSV
 */
function exportCSV() {
  dt.value.exportCSV()
}
</script>
